import {
  Card,
  CardContent,
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  LinearProgress,
} from "@mui/material";
import { Celebration, Circle } from "@mui/icons-material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const TopPerformersCard = ({ topUsers }) => {
  const getOnlineStatus = (lastVisited) => {
    if (!lastVisited) return { label: "Never", color: "default", dot: "#grey" };

    const now = dayjs();
    const lastVisitedTime = dayjs(lastVisited);
    const hoursSince = now.diff(lastVisitedTime, "hour");
    const daysSince = now.diff(lastVisitedTime, "day");

    if (hoursSince < 1) {
      return { label: "Online now", color: "success", dot: "#4caf50" };
    } else if (hoursSince < 24) {
      return {
        label: `${hoursSince}h ago`,
        color: hoursSince <= 6 ? "success" : "warning",
        dot: hoursSince <= 6 ? "#4caf50" : "#ff9800",
      };
    } else if (daysSince <= 7) {
      return {
        label: `${daysSince}d ago`,
        color: daysSince <= 3 ? "warning" : "default",
        dot: daysSince <= 3 ? "#ff9800" : "#9e9e9e",
      };
    } else {
      return { label: `${daysSince}d ago`, color: "error", dot: "#f44336" };
    }
  };

  const getCompletionRate = (user) => {
    const created = user.tasksCreated || 0;
    const completed = user.tasksCompleted || 0;
    return created > 0 ? Math.round((completed / created) * 100) : 0;
  };

  return (
    <Card elevation={2}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Celebration color="warning" sx={{ mr: 1 }} />
          <Typography variant="h6" fontWeight="bold">
            Top Performers
          </Typography>
        </Box>
        <List dense>
          {topUsers.map((user, index) => {
            const onlineStatus = getOnlineStatus(user.lastVisited);
            const completionRate = getCompletionRate(user);

            return (
              <ListItem
                key={user.userId}
                sx={{
                  px: 0,
                  py: 1.5,
                  borderBottom:
                    index < topUsers.length - 1 ? "1px solid #f0f0f0" : "none",
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    sx={{
                      bgcolor:
                        index === 0
                          ? "#ffd700"
                          : index === 1
                            ? "#c0c0c0"
                            : "#cd7f32",
                      color: "white",
                      width: 40,
                      height: 40,
                      fontSize: "0.875rem",
                      fontWeight: "bold",
                    }}
                  >
                    #{index + 1}
                  </Avatar>
                </ListItemAvatar>

                <ListItemText
                  primary={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        sx={{ mb: "0 !important" }}
                      >
                        {user.name || "Anonymous"}
                      </Typography>
                      <Circle sx={{ fontSize: 8, color: onlineStatus.dot }} />
                      <Chip
                        label={onlineStatus.label}
                        size="small"
                        variant="outlined"
                        color={onlineStatus.color}
                        sx={{ height: 20, fontSize: "0.7rem" }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 0.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {user.tasksCreated || 0} tasks •{" "}
                          {user.totalVisits || 0} visits
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight="medium"
                        >
                          {completionRate}% completion
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={completionRate}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          backgroundColor: "#f0f0f0",
                          "& .MuiLinearProgress-bar": {
                            borderRadius: 2,
                            backgroundColor:
                              completionRate > 75
                                ? "#4caf50"
                                : completionRate > 50
                                  ? "#ff9800"
                                  : "#f44336",
                          },
                        }}
                      />
                    </Box>
                  }
                />
              </ListItem>
            );
          })}
        </List>
        {topUsers.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            py={3}
          >
            No user data available yet
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default TopPerformersCard;
